import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Monitor, Trash2, Link2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { isEmbeddableVideoUrl } from '@/lib/videoEmbed';

interface IntroVideoSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  introVideoUrl: string | null | undefined;
  onSave: (url: string | null) => void | Promise<void>;
}

function pickRecorderMime(): string {
  const candidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'video/webm';
}

export default function IntroVideoSettingsDialog({
  open,
  onOpenChange,
  introVideoUrl,
  onSave,
}: IntroVideoSettingsDialogProps) {
  const [urlInput, setUrlInput] = useState('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const stopStream = useCallback(() => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  useEffect(() => {
    if (open) {
      setUrlInput(introVideoUrl?.trim() || '');
    }
  }, [open, introVideoUrl]);

  useEffect(() => {
    if (!open) {
      setStream((prev) => {
        prev?.getTracks().forEach((t) => t.stop());
        return null;
      });
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setRecording(false);
      mediaRecorderRef.current = null;
      chunksRef.current = [];
    }
  }, [open]);

  useEffect(() => {
    if (!videoRef.current || !stream) return;
    videoRef.current.srcObject = stream;
    void videoRef.current.play().catch(() => {});
  }, [stream]);

  const startCamera = async () => {
    try {
      stopStream();
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: true,
      });
      setStream(s);
    } catch (error) {
      toast.error('Camera/microfoon niet beschikbaar. Controleer browserrechten.', { error });
    }
  };

  const startScreen = async () => {
    try {
      stopStream();
      const s = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });
      s.getVideoTracks()[0]?.addEventListener('ended', () => {
        stopStream();
        setRecording(false);
      });
      setStream(s);
    } catch {
      toast.error('Scherm delen geannuleerd of niet ondersteund.');
    }
  };

  const startRecording = () => {
    if (!stream) {
      toast.info('Kies eerst camera of scherm.');
      return;
    }
    chunksRef.current = [];
    const mime = pickRecorderMime();
    try {
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onerror = (event) =>
        toast.error('Opnamefout', { error: (event as Event & { error?: unknown }).error });
      mr.onstop = async () => {
        setRecording(false);
        const parts = chunksRef.current;
        chunksRef.current = [];
        const blob = new Blob(parts, { type: mime.split(';')[0] || 'video/webm' });
        if (blob.size < 100) {
          toast.error('Geen videodata; probeer opnieuw.');
          return;
        }
        setUploading(true);
        try {
          const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
          const file = new File([blob], `intro-${Date.now()}.${ext}`, { type: blob.type || 'video/webm' });
          const { url } = await api.uploadVideo(file);
          await onSave(url);
          toast.success('Videoboodschap opgeslagen');
          stopStream();
          onOpenChange(false);
        } catch (error) {
          toast.error('Upload mislukt. Controleer verbinding en bestandsgrootte (max. 100 MB).', {
            error,
          });
        } finally {
          setUploading(false);
        }
      };
      mr.start(250);
      setRecording(true);
    } catch (error) {
      toast.error('Opname starten mislukt in deze browser.', { error });
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
    } else {
      setRecording(false);
    }
    mediaRecorderRef.current = null;
  };

  const saveUrl = async () => {
    const t = urlInput.trim();
    if (!t) {
      await onSave(null);
      toast.success('Videoboodschap verwijderd');
      onOpenChange(false);
      return;
    }
    if (t.startsWith('/uploads/') || t.startsWith('http')) {
      await onSave(t);
      toast.success('Video-URL opgeslagen');
      onOpenChange(false);
      return;
    }
    toast.error('Voer een geldige URL in (https://...) of een pad dat begint met /uploads/');
  };

  const removeVideo = async () => {
    setUrlInput('');
    await onSave(null);
    toast.success('Videoboodschap verwijderd');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Videoboodschap voor de klant</DialogTitle>
          <DialogDescription>
            Deze video verschijnt in een venster wanneer de klant de offerte voor het eerst opent. Je kunt zelf opnemen,
            of een link plakken (bijv. Vidyard, YouTube of Vimeo).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="space-y-2">
            <Label>Video-URL (Vidyard, YouTube, Vimeo)</Label>
            <div className="flex gap-2">
              <Input
                placeholder="https://share.vidyard.com/watch/..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                disabled={uploading}
              />
              <Button type="button" variant="secondary" onClick={() => void saveUrl()} disabled={uploading}>
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
            {urlInput && !urlInput.startsWith('/uploads/') && !isEmbeddableVideoUrl(urlInput) && urlInput.startsWith('http') && (
              <p className="text-xs text-amber-600">
                Controleer of de link klopt; niet alle hosts kunnen ingesloten worden. Test op de gepubliceerde pagina.
              </p>
            )}
          </div>

          <div className="rounded-lg border p-3 space-y-3">
            <p className="text-sm font-medium">Of neem op in de browser</p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={startCamera} disabled={recording || uploading}>
                <Camera className="h-4 w-4 mr-1" />
                Camera
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={startScreen} disabled={recording || uploading}>
                <Monitor className="h-4 w-4 mr-1" />
                Scherm
              </Button>
            </div>
            <div
              className="relative aspect-video bg-black rounded-md overflow-hidden max-h-[200px]"
              style={{ minHeight: 120 }}
            >
              <video ref={videoRef} className="w-full h-full object-contain" playsInline muted />
              {!stream && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-white/60 p-2 text-center">
                  Preview na het kiezen van camera of scherm
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {!recording ? (
                <Button type="button" onClick={startRecording} disabled={!stream || uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start opname'}
                </Button>
              ) : (
                <Button type="button" variant="destructive" onClick={stopRecording}>
                  Stop en uploaden
                </Button>
              )}
            </div>
          </div>

          {introVideoUrl ? (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span className="truncate">Huidige: {introVideoUrl}</span>
              <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => void removeVideo()}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
